import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutData } from "@/mocks/about";

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "云体育",
  url: "https://mandarin-course.cn",
  description: "云体育是专注于体育运动的一站式在线服务平台。",
  address: {
    "@type": "PostalAddress",
    streetAddress: "奥林匹克商务区云体育大厦",
    addressLocality: "北京",
    addressRegion: "朝阳区",
    addressCountry: "CN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+86-400-888-6688",
    email: "contact@yunsports.com",
    contactType: "customer service",
    availableLanguage: ["Chinese"],
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "09:00",
    closes: "21:00",
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://mandarin-course.cn" },
    { "@type": "ListItem", position: 2, name: "关于我们" },
  ],
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 云体育品牌介绍、荣誉资质与联系方式"
        description="云体育是专注于体育运动的一站式在线服务平台，总部位于北京。了解云体育的品牌故事、发展历程、荣誉资质和联系方式。加入云体育，让运动融入你的每一天。"
        keywords="云体育,云体育平台,关于云体育,云体育联系方式,云体育品牌"
        canonicalUrl="https://mandarin-course.cn/about"
        schema={{ ...orgSchema, ...breadcrumbSchema }}
      />

      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-6">
          <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="text-center mb-16">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">{aboutData.brand.title}</h1>
            <p className="text-primary-500 text-lg md:text-xl font-medium mb-6">{aboutData.brand.subtitle}</p>
            <p className="max-w-3xl mx-auto text-foreground-600 text-sm md:text-base leading-relaxed">
              {aboutData.brand.description}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <div>
              <h2 className="text-xl font-bold text-foreground-950 mb-6">品牌使命</h2>
              <p className="text-foreground-600 text-sm leading-relaxed mb-6">{aboutData.brand.mission}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-primary-500 mb-1">500万+</div>
                  <div className="text-xs text-foreground-500">累计服务用户</div>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-accent-500 mb-1">98%</div>
                  <div className="text-xs text-foreground-500">用户满意度</div>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-secondary-500 mb-1">200+</div>
                  <div className="text-xs text-foreground-500">专业教练团队</div>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-primary-500 mb-1">24/7</div>
                  <div className="text-xs text-foreground-500">全天候服务</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground-950 mb-6">发展历程</h2>
              <div className="flex flex-col gap-0 relative">
                <div className="absolute left-[19px] top-2 bottom-2 w-px bg-background-200/70"></div>
                {aboutData.milestones.map((m, idx) => (
                  <div key={idx} className="flex items-start gap-4 relative pb-6 last:pb-0">
                    <div className="w-[10px] h-[10px] rounded-full bg-primary-500 mt-1.5 flex-shrink-0 relative z-10"></div>
                    <div>
                      <div className="text-sm font-semibold text-primary-500 mb-1">{m.year}</div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-xl font-bold text-foreground-950 mb-8 text-center">荣誉资质</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {aboutData.honors.map((h) => (
                <div
                  key={h.title}
                  className="bg-background-100 border border-background-200/70 rounded-xl p-5 text-center"
                >
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-award-line text-lg text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div className="text-xs font-semibold text-foreground-900 mb-1">{h.title}</div>
                  <div className="text-xs text-foreground-500">{h.org}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8">
                <h3 className="text-base font-semibold text-foreground-900 mb-6">联系方式</h3>
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">公司地址</div>
                      <div className="text-sm text-foreground-900">{aboutData.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">客服电话</div>
                      <div className="text-sm text-foreground-900">{aboutData.contact.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">电子邮箱</div>
                      <div className="text-sm text-foreground-900">{aboutData.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">工作时间</div>
                      <div className="text-sm text-foreground-900">{aboutData.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8">
                <h3 className="text-base font-semibold text-foreground-900 mb-6">常见问题</h3>
                <div className="flex flex-col gap-5">
                  {aboutData.faqs.map((faq, idx) => (
                    <div key={idx}>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-2">{faq.question}</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}